import { Button } from '@h2hspark/ui';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { usePublishedEntry } from './api';
import { useDocumentMetadata } from './cms-page';
import { CaseStudyMetrics } from './collection-sections-panel';
import { ContentLink } from './content-link-panel';
import { NotFoundPage } from './not-found-page';
import { RichText } from './rich-text-panel';
import { Band } from './section-band-panel';
import { list, text, type PublishedContent } from './types';

/**
 * A case study's own page (`CMS-015` AC-2).
 *
 * ## Why case studies get a page and testimonials do not
 *
 * A testimonial is a paragraph; a page of one would be a paragraph with a lot of whitespace around
 * it. A case study has a challenge, what changed, a set of figures and a body — enough that the
 * card has to summarise it, and a summary implies somewhere to go for the rest.
 *
 * ## Why `/case-studies/{slug}` rather than `/{slug}`
 *
 * `/{slug}` is already a contested address: `CMS-009` resolves an organizer profile there first
 * and a marketing page second, and the client's rule is that everything else wins before the CMS.
 * Adding a third lookup to that chain would make every unknown URL cost three requests and would
 * put case studies in the same namespace as organizer handles, where a case study could shadow —
 * or be shadowed by — an organizer nobody has created yet.
 *
 * A two-segment path collides with nothing, needs no precedence rule, and reads as what it is.
 *
 * ## Unpublished is a 404, not an error
 *
 * The delivery route serves the published snapshot only, and everything else — a draft, an
 * archived study, a slug nobody used — comes back 404 and lands on the site's own not-found page.
 * That is `CMS-003` AC-2 arriving intact at the browser.
 */
export function CaseStudyPage(): React.ReactElement {
  const { slug } = useParams<{ slug: string }>();
  const study = usePublishedEntry('caseStudy', slug);

  if (study.isLoading) return <CaseStudySkeleton />;
  if (study.data === undefined) return <NotFoundPage />;
  return <CaseStudy study={study.data} />;
}

export function CaseStudy({
  study,
}: {
  readonly study: PublishedContent;
}): React.ReactElement {
  const data = study.data;
  const headline = text(data, 'headline');
  const seo = (data['seo'] ?? {}) as Record<string, unknown>;

  useDocumentMetadata({
    title: text(seo, 'metaTitle') || headline,
    description: text(seo, 'metaDescription') || text(data, 'summary'),
    noIndex: seo['noIndex'] === true,
  });

  const fullStoryUrl = text(data, 'fullStoryUrl');

  return (
    <Band>
      <Button asChild variant="ghost" size="sm" className="-ml-2 mb-4">
        <Link to="/for-organizers">
          <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
          Case studies
        </Link>
      </Button>

      <p className="text-sm font-semibold text-primary">{text(data, 'organizationName')}</p>
      {text(data, 'format') !== '' && (
        <p className="text-sm text-muted-foreground">{text(data, 'format')}</p>
      )}
      {/* The page's only h1 — the same rule the section renderer follows, for the same reason. */}
      <h1 className="mt-3 max-w-3xl text-3xl font-bold tracking-tight sm:text-4xl">{headline}</h1>
      <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{text(data, 'summary')}</p>

      <div className="mt-10 grid gap-10 sm:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Challenge
            </h2>
            <p className="mt-2 text-base leading-relaxed">{text(data, 'challenge')}</p>
          </section>
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              What changed
            </h2>
            <p className="mt-2 text-base leading-relaxed">{text(data, 'solution')}</p>
          </section>
          {text(data, 'body') !== '' && <RichText html={text(data, 'body')} />}
          {fullStoryUrl !== '' && (
            <p className="text-sm font-semibold text-primary">
              {/* The organizer's own write-up, elsewhere. Through the allow-list like every other
                  address an editor types — an outbound link is not a safer kind of link. */}
              <ContentLink href={fullStoryUrl}>Read {text(data, 'organizationName')}’s own write-up</ContentLink>
            </p>
          )}
        </div>
        <CaseStudyMetrics metrics={list(data, 'metrics')} />
      </div>
    </Band>
  );
}

function CaseStudySkeleton(): React.ReactElement {
  return (
    <div className="mx-auto max-w-5xl space-y-6 px-6 py-16">
      <div className="h-4 w-32 animate-pulse rounded bg-muted" />
      <div className="h-10 w-2/3 animate-pulse rounded bg-muted" />
      <div className="h-64 w-full animate-pulse rounded bg-muted" />
    </div>
  );
}
